'use client';

import { subscribeToOrg } from '@api/org-api';
import {
  getUserDetails,
  subscribeToUser,
  subscribeToUserSubscriptions,
  subscribeToUserTags,
} from '@api/user-api';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { OPAQUE_TOKEN_CACHE_KEY } from '@constants/referral';
import { anonymousUserContext } from '@context/anonymous-user-context';
import { UserContext, type UserContextValues } from '@context/user-context';
import useOnce from '@hooks/use-once';
import { analytics } from '@modules/analytics';
import { isIdentityBelongToAnonUser } from '@modules/auth-helpers';
import { type FirebaseUser, auth } from '@modules/firebase';
import { logger } from '@modules/logger';
import {
  onAuthStateChanged,
  onIdTokenChanged,
  signInAnonymously,
  signInWithCustomToken,
} from 'firebase/auth';
import type { NextPage } from 'next';
import { useRouter } from 'next/compat/router';
import {
  type FC,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import type { BBOrg, StripeSubscription, User, UserTags } from 'types';

import { ExchangeOneTimeLoginTokenDocument } from './ExchangeOneTimeLoginToken.graphql';
import { GetActorDocument, type GetActorQuery } from './WithUser.graphql';

/**
 * If an unauthenticated user tries to access a certain page, they are immediately redirected to the login page, this method is used to determine if we should direct the user back to the original page or not
 * @deprecated Use `useUserProfile` from `@modules/user-profile/UserProfileProvider` instead.
 */
export const willRedirectBackAfterLogin = (path: string): boolean => {
  // regex pattern for
  // [workspaceId]/cards/personal/[cardId]/preview?anythingAfter
  // [workspaceId]/cards/team/[cardId]/preview?anythingAfter
  const regex = /^\/[a-zA-Z0-9]+\/cards\/(personal|team)\/[a-zA-Z0-9]+\/preview(\?.*)?$/;
  return regex.test(path);
};

/**
 * FullyLoaded does not mean we have all the values, it means we are not expecting anything more. All the values can still be undefined
 * @deprecated Use `useUserProfile` from `@modules/user-profile/UserProfileProvider` instead.
 */
export function isFullyLoaded(userDetails: UserReducerState): boolean {
  const { loadedDetails, user } = userDetails;

  const valuesNeeded: LoadedDetailsValues[] = ['user', 'userTags', 'userSubscriptions'];

  if (user?.bbOrgId) {
    valuesNeeded.push('org');
  }

  if (loadedDetails.includes('user') && user?.isAnonymous === false) {
    valuesNeeded.push('actor');
  }

  return valuesNeeded.every((valueNeeded) => loadedDetails.includes(valueNeeded));
}

type LoadedDetailsValues = Readonly<keyof UserContextValues>;

export interface UserReducerState extends UserContextValues {
  loadedDetails: LoadedDetailsValues[];
}

type UserAction =
  | { type: 'UPDATE_IDENTITY'; value: FirebaseUser | undefined }
  | { type: 'UPDATE_USER'; value: User | undefined }
  | { type: 'UPDATE_USER_TAGS'; value: UserTags | undefined }
  | { type: 'UPDATE_USER_SUBSCRIPTIONS'; value: StripeSubscription[] }
  | { type: 'UPDATE_BB_ORG'; value: BBOrg | undefined }
  | { type: 'FINISH_LOADING_FIREBASE' }
  | {
      type: 'UPDATE_ACTOR';
      value: GetActorQuery['actor'] | null | undefined;
    };

function userReducer(state: UserReducerState, action: UserAction): UserReducerState {
  switch (action.type) {
    case 'UPDATE_IDENTITY': {
      const newState = { ...state, identity: action.value };
      return { ...newState, loading: !isFullyLoaded(newState) };
    }
    case 'UPDATE_USER': {
      const newState = { ...state, user: action.value };
      return { ...newState, loading: !isFullyLoaded(newState) };
    }
    case 'UPDATE_USER_TAGS': {
      const newState = {
        ...state,
        userTags: action.value,
      };
      return { ...newState, loading: !isFullyLoaded(newState) };
    }
    case 'UPDATE_USER_SUBSCRIPTIONS': {
      const newState = {
        ...state,
        userSubscriptions: action.value,
      };
      return { ...newState, loading: !isFullyLoaded(newState) };
    }
    case 'UPDATE_BB_ORG': {
      const newState = {
        ...state,
        org: action.value,
        loadedDetails: [...new Set<LoadedDetailsValues>([...state.loadedDetails, 'org'])],
      };
      return { ...newState, loading: !isFullyLoaded(newState) };
    }
    case 'FINISH_LOADING_FIREBASE': {
      const newState = {
        ...state,
        loadedDetails: [
          ...new Set<LoadedDetailsValues>([...state.loadedDetails, 'user', 'userTags', 'userSubscriptions']),
        ],
      };
      return { ...newState, loading: !isFullyLoaded(newState) };
    }
    case 'UPDATE_ACTOR': {
      const newState = {
        ...state,
        actor: action.value,
        loadedDetails: [...new Set<LoadedDetailsValues>([...state.loadedDetails, 'actor'])],
      };
      return { ...newState, loading: !isFullyLoaded(newState) };
    }
  }
}

/**
 * If the user is authenticated with firebase this will fetch all their details. It also caches any updates and populates from
 * the cache if available. Without this all premium features will be locked or not rendered until the user data is retrived, which
 * leads to a crappy experience when you switch pages (premium features are momentarily removed before being restored again).
 * This has the added benefit of making page changes very snappy.
 * @deprecated Use `useUserProfile` from `@modules/user-profile/UserProfileProvider` instead.
 */
export function useUserDetails(): UserContextValues {
  const [getActor] = useLazyQuery(GetActorDocument, {
    errorPolicy: 'all', // Must be all so that when errors are thrown, the data is still returned
  });
  const [userDetails, dispatch] = useReducer(userReducer, {
    identity: undefined,
    user: undefined,
    userTags: undefined,
    userSubscriptions: [],
    org: undefined,
    loadedDetails: [],
    actor: undefined,
    loading: true,
  });

  // Update BBorg
  useEffect(() => {
    if (!userDetails.user?.bbOrgId) return;

    function initialise(orgId: string) {
      const cachedBBOrg = localStorage.getItem('bbOrg');

      if (cachedBBOrg) {
        dispatch({ type: 'UPDATE_BB_ORG', value: JSON.parse(cachedBBOrg) });
      }

      const unsubscribeFromBBOrg = subscribeToOrg(orgId, (bbOrg) => {
        localStorage.setItem('bbOrg', JSON.stringify(bbOrg));
        dispatch({ type: 'UPDATE_BB_ORG', value: bbOrg });
      });

      return unsubscribeFromBBOrg;
    }

    return initialise(userDetails.user?.bbOrgId);
  }, [userDetails.user?.bbOrgId]);

  // Update User
  useOnce((preventSecondRun) => {
    preventSecondRun();
    const cachedUser = localStorage.getItem('user');
    const cachedUserTags = localStorage.getItem('userTags');
    const cachedUserSubscriptions = localStorage.getItem('userSubscriptions');

    const identity = auth.currentUser;

    if (identity) {
      dispatch({ type: 'UPDATE_IDENTITY', value: identity || undefined });
    }

    if (cachedUser) {
      const user = JSON.parse(cachedUser);
      if (userDetails?.identity?.email) {
        user.signInEmailAddress = userDetails.identity.email;
      }
      dispatch({ type: 'UPDATE_USER', value: user });
    }

    if (cachedUserTags) {
      dispatch({ type: 'UPDATE_USER_TAGS', value: JSON.parse(cachedUserTags) });
    }

    if (cachedUserSubscriptions) {
      dispatch({ type: 'UPDATE_USER_SUBSCRIPTIONS', value: JSON.parse(cachedUserSubscriptions) });
    }

    if (identity && cachedUser && cachedUserTags && cachedUserSubscriptions) {
      dispatch({ type: 'FINISH_LOADING_FIREBASE' });
    }
  }, []);

  // Firebase listeners
  useEffect(() => {
    function initialiseChangeListeners() {
      if (!userDetails.identity) {
        return;
      }

      const unsubscribeFromUser = subscribeToUser(userDetails.identity.uid, (user) => {
        if (!user) {
          localStorage.removeItem('user');
          dispatch({ type: 'UPDATE_USER', value: undefined });
        } else {
          if (userDetails.identity?.email) {
            user.signInEmailAddress = userDetails.identity.email;
          }

          localStorage.setItem('user', JSON.stringify(user));
          dispatch({ type: 'UPDATE_USER', value: user });
        }
      });

      const unsubscribeFromUserTags = subscribeToUserTags(userDetails.identity.uid, (userTags) => {
        if (!userTags) {
          localStorage.removeItem('userTags');
        } else {
          localStorage.setItem('userTags', JSON.stringify(userTags));
        }
        dispatch({ type: 'UPDATE_USER_TAGS', value: userTags });
      });

      const unsubscribeFromUserSubscriptions = subscribeToUserSubscriptions(
        userDetails.identity.uid,
        (userSubscriptions) => {
          localStorage.setItem('userSubscriptions', JSON.stringify(userSubscriptions));
          dispatch({ type: 'UPDATE_USER_SUBSCRIPTIONS', value: userSubscriptions });
        },
      );

      return () => {
        unsubscribeFromUser();
        unsubscribeFromUserTags();
        unsubscribeFromUserSubscriptions();
      };
    }

    return initialiseChangeListeners();
  }, [userDetails.identity]);

  // Update Actor
  useOnce(
    async (preventSecondRun) => {
      // Before creating a profile we want to make sure that the user has at least an email.
      if (!userDetails.user?.signInEmailAddress) {
        return;
      }

      // Prevents the second run on this page, this will still run when the user is changing page
      preventSecondRun();

      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const search = new URLSearchParams(window.location.search);
        const gclid = search.get('gclid');
        const fbclid = search.get('gclid');
        const response = await getActor({
          variables: {
            timezone: timezone,
            gclid,
            fbclid,
          },
        });
        dispatch({ type: 'UPDATE_ACTOR', value: response.data?.actor });
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Error calling this endpoint');
        logger.error('Update actor failed', { error: err });
      }
    },
    [userDetails],
  );

  /**
   * Handles when a user is logged in or logged out, or when their refresh token is refreshed
   */
  const observeAuthStateChanged = useCallback(async (firebaseUser: FirebaseUser | null) => {
    try {
      if (!firebaseUser) {
        dispatch({ type: 'UPDATE_IDENTITY', value: undefined });
        dispatch({ type: 'UPDATE_USER', value: undefined });
        dispatch({ type: 'UPDATE_USER_TAGS', value: undefined });
        dispatch({ type: 'UPDATE_USER_SUBSCRIPTIONS', value: [] });
        return;
      }

      const newUserDetails = await getUserDetails(firebaseUser.uid);

      dispatch({ type: 'UPDATE_IDENTITY', value: firebaseUser });
      if (newUserDetails.user) {
        localStorage.setItem('user', JSON.stringify(newUserDetails.user));

        dispatch({
          type: 'UPDATE_USER',
          value: {
            ...newUserDetails.user,
            isAnonymous: isIdentityBelongToAnonUser(firebaseUser),
            signInEmailAddress: firebaseUser.email || undefined,
          },
        });
      } else {
        localStorage.removeItem('user');
      }

      if (newUserDetails.userTags) {
        localStorage.setItem('userTags', JSON.stringify(newUserDetails.userTags));

        dispatch({ type: 'UPDATE_USER_TAGS', value: newUserDetails.userTags });
      } else {
        localStorage.removeItem('userTags');
      }

      if (newUserDetails.userSubscriptions) {
        localStorage.setItem('userSubscriptions', JSON.stringify(newUserDetails.userSubscriptions));

        dispatch({ type: 'UPDATE_USER_SUBSCRIPTIONS', value: newUserDetails.userSubscriptions });
      } else {
        localStorage.removeItem('userSubscriptions');
      }

      dispatch({ type: 'FINISH_LOADING_FIREBASE' });
      // Purposefully don't await this.
    } catch (error: unknown) {
      logger.error('observeAuthStateChanged callback failed.', { error });
    } finally {
      dispatch({ type: 'FINISH_LOADING_FIREBASE' });
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, observeAuthStateChanged);

    return unsubscribe;
  }, [observeAuthStateChanged]);

  const result = useMemo(
    () => ({
      identity: userDetails.identity,
      user: userDetails.user,
      userTags: userDetails.userTags,
      userSubscriptions: userDetails.userSubscriptions,
      org: userDetails.org,
      loading: userDetails.loading,
      actor: userDetails.actor,
      completedOnboardingTours: userDetails.actor?.user?.completed_onboarding_tours,
    }),
    [userDetails],
  );

  return result;
}

/**
 * @deprecated Use `useUserProfile` from `@modules/user-profile/UserProfileProvider` instead.
 */
export function useAnonymousUser(): FirebaseUser | undefined {
  const router = useRouter();
  const [user, setUser] = useState<FirebaseUser>();
  const isCreating = useRef(false);
  const [exchangeOneTimeLoginToken] = useMutation(ExchangeOneTimeLoginTokenDocument);

  const cachedOpaqueToken =
    typeof window !== 'undefined' ? window.localStorage.getItem(OPAQUE_TOKEN_CACHE_KEY) : undefined;
  const opaqueToken = useMemo(
    () => router?.query['opaque_token'] ?? cachedOpaqueToken,
    [router?.query, cachedOpaqueToken],
  );

  const createAnonymousUser = useCallback(async () => {
    try {
      isCreating.current = true;

      let firebaseUser = auth.currentUser;

      if (!firebaseUser) {
        const userCredential = await signInAnonymously(auth);
        firebaseUser = userCredential.user;
      }

      if (!firebaseUser) {
        throw new Error('Still no user on anonymous sign in');
      }

      isCreating.current = false;
      setUser(firebaseUser);
    } catch (error) {
      isCreating.current = false;
    }
  }, []);

  /** Signs in anonymously, either with opaque token, or creates a new user */
  const signInAnonymousUser = useCallback(
    async (opaqueToken: string | undefined) => {
      try {
        isCreating.current = true;

        let firebaseUser = auth.currentUser;

        if (!firebaseUser) {
          let customToken: string | null | undefined = null;
          if (opaqueToken) {
            const mutationResult = await exchangeOneTimeLoginToken({
              variables: {
                opaque_token: opaqueToken,
              },
              context: {
                isUnauthenticatedRequest: true,
              },
            });

            customToken = mutationResult.data?.exchangeOneTimeLoginToken;
          }
          if (!customToken) {
            // fall back to creating anon user if something gone wrong
            isCreating.current = false;
            createAnonymousUser();

            return;
          }

          await signInWithCustomToken(auth, customToken);

          firebaseUser = auth.currentUser;
          if (firebaseUser) {
            window.localStorage.removeItem(OPAQUE_TOKEN_CACHE_KEY); // delete the token from local storage
          }
        }

        if (!firebaseUser) {
          throw new Error('Still no user on anonymous sign in');
        }

        isCreating.current = false;
        setUser(firebaseUser);
      } catch (error) {
        isCreating.current = false;
        logger.error('Failed to sign in using custom token', { error });
        createAnonymousUser(); // create anon user if for some reason session continuity does not work
      }
    },
    [createAnonymousUser, exchangeOneTimeLoginToken],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    if (!router?.isReady) {
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (identity: FirebaseUser | null) => {
      if (!identity || !isCreating.current) {
        const hasOpaqueToken = opaqueToken && !Array.isArray(opaqueToken) ? opaqueToken : undefined;
        signInAnonymousUser(hasOpaqueToken);
      }
    });

    return unsubscribe;
  }, [createAnonymousUser, router?.isReady, opaqueToken, signInAnonymousUser]);

  return user;
}

interface LayoutWithUserProps {
  children: ReactNode;
  redirect?: boolean;
  redirectPath?: string;
}

/**
 * Wraps a child with the user context.
 * For use in new `layout.ts` files.
 * @deprecated Use `useUserProfile` from `@modules/user-profile/UserProfileProvider` instead.
 */
export function Layout_WithUser({
  children,
  redirect = true,
  redirectPath,
}: LayoutWithUserProps): ReactElement {
  return (
    <UserProvider redirect={redirect} redirectPath={redirectPath}>
      {children}
    </UserProvider>
  );
}

// TEMP: Hermes is working on authorization to simplify this work - this is a temporary solution
// that will prevent new users from seeing an unpaid business account
// as we are fixing this on the graphQl side

function transformUserDetails(userDetails: UserContextValues): UserContextValues {
  // Here we remove the bbOrgId that we get from firestore if we do not have a matching workspace from the actor query

  const bbOrgId = userDetails.user?.bbOrgId;
  const actorWorkspacesContainsBbOrg = bbOrgId
    ? userDetails.actor?.workspaces?.some((workspace) => workspace.id === bbOrgId)
    : false;

  return {
    ...userDetails,
    user: userDetails.user
      ? {
          ...userDetails.user,
          bbOrgId: actorWorkspacesContainsBbOrg ? bbOrgId : undefined,
        }
      : undefined,
  };
}

/**
 * @deprecated Use `useUserProfile` from `@modules/user-profile/UserProfileProvider` instead.
 */
export function WithAnonymousUser(
  PageComponent: NextPage | FC<PropsWithChildren<unknown>>,
): (props: { [key: string]: unknown }) => ReactNode {
  // biome-ignore lint/correctness/noNestedComponentDefinitions: historic
  const WithUser = (props: { [key: string]: unknown }) => {
    const anonymousUser = useAnonymousUser();
    const userDetails = useUserDetails();

    return (
      <UserContext.Provider value={userDetails}>
        <anonymousUserContext.Provider value={{ user: anonymousUser }}>
          <PageComponent {...props} />
        </anonymousUserContext.Provider>
      </UserContext.Provider>
    );
  };

  return WithUser;
}

interface UserProviderProps {
  children: ReactNode;
  redirect?: boolean;
  redirectPath?: string;
}

function UserProvider({ children, redirect = true, redirectPath = '/login' }: UserProviderProps) {
  const userDetails = useUserDetails();
  const router = useRouter();

  useEffect(() => {
    if (router?.isReady && !userDetails.loading && !userDetails.identity && redirect) {
      // asPath only includes what comes after localhost:3000/ or blinq.me/ so we don't have to worry about bad actors redirecting to malicious sites, worse that will happen is they get a 404 for our site
      const goTo = router.asPath;

      if (willRedirectBackAfterLogin(goTo)) {
        router.push(`${redirectPath}?goto=${goTo}`);
        return;
      }

      router.push(`${redirectPath}`);
      return;
    }
  }, [userDetails, router, redirect, redirectPath]);

  return <UserContext.Provider value={transformUserDetails(userDetails)}>{children}</UserContext.Provider>;
}
